import styled from 'styled-components'
import { media } from '../../../../utils/mediaQuery'

export const LegendContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};

  @media (max-width: 640px) {
    margin-top: ${({ theme }) => theme.spacing.medium};
    margin-bottom: ${({ theme }) => theme.spacing.medium};
  }
`

export const LegendHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const ChevronIcon = styled.span<{ $isOpen: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  transition: ${({ theme }) => theme.transitions.fast};
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`

export const LegendContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;
  padding: ${({ theme }) => `${theme.spacing.medium} ${theme.spacing.large}`};
  background: ${({ theme }) => theme.colors.background.primary};

  ${media.tabletDown} {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) =>
      `${theme.spacing.small} ${theme.spacing.default}`};
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const LegendColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: 0 ${({ theme }) => theme.spacing.medium};

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.border.light};
  }

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  ${media.tabletDown} {
    padding: 0;
    &:not(:last-child) {
      border-right: none;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
      padding-bottom: ${({ theme }) => theme.spacing.medium};
    }
  }
`

export const ColumnTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const AgreementBox = styled.div<{ $type: 'identical' | 'conflict' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $type }) => ($type === 'identical' ? '#f0fdf4' : '#fef2f2')};
  border: 1px solid
    ${({ $type }) => ($type === 'identical' ? '#bbf7d0' : '#fecaca')};
`

export const AgreementLabel = styled.div<{ $type: 'identical' | 'conflict' }>`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ $type }) => ($type === 'identical' ? '#198754' : '#dc3545')};
`

export const UniqueBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const ConflictNote = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-style: italic;
`

export const StrikethroughExample = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: #868e96;
  text-decoration: line-through;
`

export const DifferenceExample = styled.span<{ $positive: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $positive, theme }) =>
    $positive ? theme.colors.success : theme.colors.danger};
`

export const DottedUnderline = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline dotted;
  text-decoration-color: ${({ theme }) => theme.colors.border.default};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const EmptyDash = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-style: italic;
`
